import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, Instagram, Facebook, Settings, Sparkles, Search, Heart, Shield, User } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Header = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { getCartCount } = useCart();
  const { getWishlistCount } = useWishlist();
  const { customer } = useCustomerAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const navigate = useNavigate();
  const cartCount = getCartCount();
  const wishlistCount = getWishlistCount();

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/hookahs', label: t.nav.hookahs },
    { to: '/sets', label: t.nav.sets },
    { to: '/accessories', label: t.nav.accessories },
    { to: '/warranty', label: language === 'hu' ? 'Garancia' : 'Warranty' },
    { to: '/bestsellers', label: language === 'hu' ? 'Legnépszerűbb' : 'Bestsellers' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-zinc-800 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo - Text only */}
          <Link to="/" className="flex items-center">
            <span className="text-lg sm:text-xl font-bold text-white tracking-tight">
              Shisha<span className="text-zinc-400">Vibes</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium text-zinc-400 hover:text-white transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
            {/* Sale Button - Special styling */}
            <Link
              to="/sale"
              className="relative group"
            >
              <span className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-bold text-black bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500 rounded-full hover:from-yellow-300 hover:via-orange-300 hover:to-red-400 transition-all duration-300 shadow-lg shadow-orange-500/25">
                <Sparkles className="h-4 w-4" />
                {t.nav.sale}
              </span>
              <span className="absolute -inset-0.5 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500 rounded-full blur opacity-30 group-hover:opacity-50 transition-opacity" />
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search */}
            <button
              onClick={() => navigate('/search')}
              className="text-zinc-400 hover:text-white transition-colors p-1"
              title={language === 'hu' ? 'Keresés' : 'Search'}
              data-testid="search-button"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-zinc-400 hover:text-white transition-colors px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500"
            >
              <span className={language === 'hu' ? 'text-white' : ''}>HU</span>
              <span className="text-zinc-600">/</span>
              <span className={language === 'en' ? 'text-white' : ''}>EN</span>
            </button>

            {/* Facebook Link */}
            <a
              href="https://www.facebook.com/profile.php?id=61583331609027"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:block text-zinc-400 hover:text-white transition-colors p-1"
              title="Facebook"
              data-testid="header-facebook-link"
            >
              <Facebook className="h-5 w-5" />
            </a>

            {/* Instagram Link */}
            <a
              href="https://www.instagram.com/_shishavibes._/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:block text-zinc-400 hover:text-white transition-colors p-1"
              title="Instagram"
              data-testid="header-instagram-link"
            >
              <Instagram className="h-5 w-5" />
            </a>

            {/* Wishlist */}
            <button
              onClick={() => navigate('/wishlist')}
              className="relative text-zinc-400 hover:text-white transition-colors p-1"
              data-testid="wishlist-button"
              title={language === 'hu' ? 'Kívánságlista' : 'Wishlist'}
            >
              <Heart className="h-5 w-5" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            {/* Account */}
            <button
              onClick={() => navigate('/account')}
              className="relative text-zinc-400 hover:text-white transition-colors p-1"
              data-testid="account-button"
              title={customer ? (language === 'hu' ? 'Fiókom' : 'My account') : (language === 'hu' ? 'Belépés' : 'Login')}
            >
              <User className="h-5 w-5" />
              {customer && (
                <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 border border-black" />
              )}
            </button>

            {/* Admin Link */}
            <button
              onClick={() => navigate('/admin')}
              className="hidden sm:block text-zinc-400 hover:text-white transition-colors p-1"
              title="Admin"
            >
              <Settings className="h-5 w-5" />
            </button>

            {/* Cart */}
            <button
              onClick={() => navigate('/cart')}
              className="relative text-zinc-400 hover:text-white transition-colors p-1"
              data-testid="cart-button"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white text-black text-xs font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Mobile Menu */}
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-zinc-950 border-zinc-800">
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                  {/* Sale Link in Mobile */}
                  <Link
                    to="/sale"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 text-lg font-bold text-orange-400 hover:text-orange-300 transition-colors"
                  >
                    <Sparkles className="h-5 w-5" />
                    {t.nav.sale}
                  </Link>
                  {/* Wishlist in Mobile */}
                  <Link
                    to="/wishlist"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                  >
                    <Heart className="h-5 w-5" />
                    {language === 'hu' ? 'Kívánságlista' : 'Wishlist'}
                    {wishlistCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{wishlistCount}</span>
                    )}
                  </Link>
                  {/* Warranty in Mobile */}
                  <Link
                    to="/warranty"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                  >
                    <Shield className="h-5 w-5" />
                    {language === 'hu' ? 'Garancia' : 'Warranty'}
                  </Link>
                  {/* Account in Mobile */}
                  <Link
                    to="/account"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                    data-testid="mobile-account-link"
                  >
                    <User className="h-5 w-5" />
                    {customer ? (language === 'hu' ? 'Fiókom' : 'My account') : (language === 'hu' ? 'Belépés' : 'Login')}
                  </Link>
                  {/* Admin in Mobile */}
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                  >
                    <Settings className="h-5 w-5" />
                    Admin
                  </Link>
                  <a
                    href="https://www.facebook.com/profile.php?id=61583331609027"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                  >
                    <Facebook className="h-5 w-5" />
                    Facebook
                  </a>
                  <a
                    href="https://www.instagram.com/_shishavibes._/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                  >
                    <Instagram className="h-5 w-5" />
                    Instagram
                  </a>
                  {/* Language Toggle in Mobile */}
                  <button
                    onClick={() => { toggleLanguage(); setIsMenuOpen(false); }}
                    className="flex items-center gap-2 text-lg font-medium text-zinc-300 hover:text-white transition-colors"
                  >
                    <span className={language === 'hu' ? 'text-white font-bold' : ''}>HU</span>
                    <span className="text-zinc-600">/</span>
                    <span className={language === 'en' ? 'text-white font-bold' : ''}>EN</span>
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
